import React, { useRef, useState } from "react";
import { roomsApi } from "./useRoom";
import { useRunner } from "./parts";
import { KIND_LABEL } from "./roomModel.mjs";
import { uploadPdfCover } from "./pdfCover";

/*
 * ADD TO THE LIBRARY (Library board, the top card): drop a PDF or images, or
 * paste a link; give it the title the prospect reads. Several images make one
 * "Example ads" item. Used on the library page and inside the picker's "Upload
 * something new…".
 *
 * Props: onAdded(item), compact (the picker's narrower layout)
 */
const ACCEPT = "application/pdf,image/png,image/jpeg,image/gif,image/webp,.docx,.pptx";

function guessKind(files, url) {
  if (url) return "link";
  if (files.length && files.every((f) => /^image\//.test(f.type))) return "example_ads";
  return "deck";
}

function guessTitle(files) {
  if (!files.length) return "";
  return files[0].name.replace(/\.[a-z0-9]+$/i, "").replace(/[_-]+/g, " ").trim();
}

export default function AddToLibrary({ onAdded, compact = false }) {
  const [files, setFiles] = useState([]);
  const [url, setUrl] = useState("");
  const [title, setTitle] = useState("");
  const [kind, setKind] = useState("");
  const [over, setOver] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const run = useRunner(setBusy, setErr);
  const input = useRef(null);

  const pick = (list) => {
    const arr = Array.from(list || []);
    if (!arr.length) return;
    setFiles(arr);
    setUrl("");
    if (!title) setTitle(guessTitle(arr));
  };

  const effectiveKind = kind || guessKind(files, url.trim());
  const ready = (files.length || url.trim()) && title.trim();

  const add = () =>
    run(async () => {
      const ids = [];
      for (const f of files) {
        const { file } = await roomsApi.upload(f);
        ids.push(file.id);
      }
      // A PDF's first page becomes its card on the room (a PowerPoint's own
      // thumbnail is read on the server).
      const cover = files[0] && files[0].type === "application/pdf" ? await uploadPdfCover(files[0], roomsApi.upload) : null;
      const { item } = await roomsApi.addLink({ kind: effectiveKind, title: title.trim(), url: url.trim() || null, file_ids: ids, cover_file_id: cover });
      setFiles([]);
      setUrl("");
      setTitle("");
      setKind("");
      if (onAdded) onAdded(item);
    });

  const dropText = files.length
    ? files.length === 1
      ? files[0].name
      : `${files.length} files · ${files.map((f) => f.name).join(", ")}`
    : null;

  return (
    <div className={compact ? "rm-stack" : "rm-addlib"} style={compact ? { display: "flex", flexDirection: "column", gap: 10 } : undefined}>
      <div
        className={`rm-drop${over ? " is-over" : ""}${files.length ? " has-file" : ""}`}
        role="button"
        tabIndex={0}
        onClick={() => input.current && input.current.click()}
        onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && input.current && input.current.click()}
        onDragOver={(e) => {
          e.preventDefault();
          setOver(true);
        }}
        onDragLeave={() => setOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setOver(false);
          pick(e.dataTransfer.files);
        }}
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M12 15V3" />
          <path d="m7 8 5-5 5 5" />
          <path d="M5 21h14" />
        </svg>
        <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {dropText || (
            <>
              Drop a PDF or images, or <u style={{ fontWeight: 600 }}>browse</u>
            </>
          )}
        </span>
        <input ref={input} type="file" accept={ACCEPT} multiple hidden onChange={(e) => pick(e.target.files)} />
      </div>
      <div className="rm-field">
        <label htmlFor="rm-lib-link">…or paste a link</label>
        <input
          id="rm-lib-link"
          className="rm-input"
          placeholder="Google Slides, Loom, any URL"
          value={url}
          onChange={(e) => {
            setUrl(e.target.value);
            if (e.target.value) setFiles([]);
          }}
        />
      </div>
      <div className="rm-field">
        <label htmlFor="rm-lib-title">Title</label>
        <input id="rm-lib-title" className="rm-input" placeholder="What the prospect sees on the card" value={title} onChange={(e) => setTitle(e.target.value)} />
      </div>
      <div className="rm-field">
        <label htmlFor="rm-lib-kind">Kind</label>
        <select id="rm-lib-kind" className="rm-select" value={effectiveKind} onChange={(e) => setKind(e.target.value)}>
          {Object.entries(KIND_LABEL).map(([k, l]) => (
            <option key={k} value={k}>{l}</option>
          ))}
        </select>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 4, alignItems: compact ? "flex-start" : "stretch" }}>
        <button type="button" className="rm-btn rm-btn--primary" disabled={!ready || busy} onClick={add}>
          {busy ? (files.length ? "Uploading…" : "Adding…") : "Add to library"}
        </button>
        {err && <span className="rm-err">{err}</span>}
      </div>
    </div>
  );
}
